import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Send,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Timesheet, TimesheetStatus } from '../types';
import Layout from '../components/Layout/Layout';

const Timesheets: React.FC = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: '',
  });

  useEffect(() => {
    loadTimesheets();
  }, [filters]);


  const loadTimesheets = async () => {
    try {
      setLoading(true);
      
      // Load all timesheets for current user
      const apiFilters: any = {};
      if (filters.status) {
        apiFilters.status = filters.status;
      }
      
      const allTimesheets = await apiService.getTimesheets(apiFilters);
      
      // Apply status filter if set (additional client-side filtering if needed)
      let filteredTimesheets = allTimesheets;
      if (filters.status) {
        filteredTimesheets = allTimesheets.filter(t => t.status === filters.status);
      }
      
      // Sort by date (most recent first)
      filteredTimesheets.sort((a, b) => {
        const dateA = new Date(a.fromDate).getTime();
        const dateB = new Date(b.fromDate).getTime();
        return dateB - dateA;
      });
      
      setTimesheets(filteredTimesheets);
    } catch (error) {
      console.error('Failed to load timesheets:', error);
      setTimesheets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, timesheet: Timesheet) => {
    setAnchorEl(event.currentTarget);
    setSelectedTimesheet(timesheet);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTimesheet(null);
  };

  const handleSubmitTimesheet = async (timesheetId: string) => {
    try {
      await apiService.submitTimesheet(timesheetId);
      setSuccess('Timesheet submitted successfully!');
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
      loadTimesheets();
      handleMenuClose();
    } catch (error) {
      console.error('Failed to submit timesheet:', error);
      setError('Failed to submit timesheet');
      setSuccess(null);
    }
  };

  const getStatusColor = (status: TimesheetStatus) => {
    switch (status) {
      case TimesheetStatus.APPROVED:
        return 'success';
      case TimesheetStatus.REJECTED:
        return 'error';
      case TimesheetStatus.SUBMITTED:
        return 'warning';
      default:
        return 'default';
    }
  };

  const canEdit = (timesheet: Timesheet) => {
    return timesheet.status === TimesheetStatus.DRAFT || timesheet.status === TimesheetStatus.REJECTED;
  };

  const canSubmit = (timesheet: Timesheet) => {
    return timesheet.status === TimesheetStatus.DRAFT;
  };

  const getTotalHours = (timesheet: Timesheet) => {
    if (!timesheet.items) return 0;
    const parseHours = (hours: any): number => {
      if (typeof hours === 'string') {
        return parseFloat(hours) || 0;
      }
      return hours || 0;
    };
    
    return timesheet.items.reduce((total, item) => {
      return total + 
        parseHours(item.mondayHours) + 
        parseHours(item.tuesdayHours) + 
        parseHours(item.wednesdayHours) + 
        parseHours(item.thursdayHours) + 
        parseHours(item.fridayHours) + 
        parseHours(item.saturdayHours) + 
        parseHours(item.sundayHours);
    }, 0);
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Timesheets</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/timesheets/new')}
        >
          New Timesheet
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}


      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Status Filter"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={TimesheetStatus.DRAFT}>Draft</MenuItem>
              <MenuItem value={TimesheetStatus.SUBMITTED}>Submitted</MenuItem>
              <MenuItem value={TimesheetStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={TimesheetStatus.REJECTED}>Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={() => setFilters({ status: '' })}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">
              {timesheets.length} timesheet{timesheets.length !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {timesheets.length === 0 ? (
        <Alert severity="info">
          No timesheets found. Create a new timesheet to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Week</TableCell>
                <TableCell>Total Hours</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timesheets.map((timesheet) => (
                <TableRow key={timesheet.id}>
                  <TableCell>
                    {new Date(timesheet.fromDate).toLocaleDateString()} -{' '}
                    {new Date(timesheet.toDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{getTotalHours(timesheet).toFixed(2)} hours</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={timesheet.status}
                        color={getStatusColor(timesheet.status)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {timesheet.createdAt
                      ? new Date(timesheet.createdAt).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, timesheet)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate(`/timesheets/${selectedTimesheet?.id}`);
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View
        </MenuItem>
        {selectedTimesheet && canEdit(selectedTimesheet) && (
          <MenuItem
            onClick={() => {
              navigate(`/timesheets/${selectedTimesheet.id}`);
              handleMenuClose();
            }}
          >
            <Edit sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {selectedTimesheet && canSubmit(selectedTimesheet) && (
          <MenuItem
            onClick={() => handleSubmitTimesheet(selectedTimesheet.id)}
          >
            <Send sx={{ mr: 1 }} />
            Submit
          </MenuItem>
        )}
      </Menu>
    </Layout>
  );
};

export default Timesheets;